import { generateChartConfig } from '@sensehawk/chart-generator';
import { applyClientSideFormatting, applyEChartsUnifiedInterTheme, applyFusionUnifiedInterTheme, handleEchartsResize } from '@sensehawk/chart-generator/formatters';
import * as echarts from 'echarts';
import { cloneDeep, isEqual, isNil } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { computed, nextTick } from 'vue';
import { AXES_NAMES_SUPPORTED_CHARTS, BI_DEFAULT_PALETTE_COLORS, BI_HEATMAP_PALETTES, CHART_TO_VALUE_TYPE_MAP, DUAL_Y_AXIS_SUPPORTED_CHARTS, SERIES_SUPPORTED_CHARTS } from '~/bi/constants/bi-constants';
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { import_fusioncharts } from '~/common/utils/package.utils.js';

export function useBiChartBuilderHelpers() {
  const bi_store = useBiStore();
  const { widget_builder_data, chart_builder_columns_data_types, widget_builder_config, is_chart_builder_form_valid, echarts_resize_observers } = storeToRefs(bi_store);

  const { $t } = useCommonImports();

  // All the widget types supported in BI through the chart builder page (rich text excluded because it doesn't go through the data builder -> chart builder flow)
  const chart_types = [
    // eslint-disable-next-line no-undef
    ['table', $t('Table'), IconHawkTableTwo],
    // eslint-disable-next-line no-undef
    ['pivot_table', $t('Pivot table'), IconHawkPivotTable],
    // eslint-disable-next-line no-undef
    ['column_chart', $t('Column chart'), IconHawkHorizontalBarChartOne],
    // eslint-disable-next-line no-undef
    ['horizontalBar_chart', $t('Bar chart'), IconHawkHorizontalBarChartOne],
    // eslint-disable-next-line no-undef
    ['line_chart', $t('Line chart'), IconHawkLineChartUpOne],
    // eslint-disable-next-line no-undef
    ['area_chart', $t('Area chart'), IconHawkAreaChart],
    // eslint-disable-next-line no-undef
    ['mixed_chart', $t('Mixed chart'), IconHawkMixChart],
    // eslint-disable-next-line no-undef
    ['pie_chart', $t('Pie chart'), IconHawkPieChartThree],
    // eslint-disable-next-line no-undef
    ['donut_chart', $t('Doughnut chart'), IconHawkDoughnutChart],
    // eslint-disable-next-line no-undef
    ['scatter_chart', $t('Scatter chart'), IconHawkScatterChart],
    // eslint-disable-next-line no-undef
    ['gauge_chart', $t('Gauge chart'), IconHawkGaugeChart],
    // eslint-disable-next-line no-undef
    ['progress_chart', $t('Progress chart'), IconHawkProgressChart],
    // eslint-disable-next-line no-undef
    ['heatmap_chart', $t('Heatmap chart'), IconHawkHeatmapChart],
    // eslint-disable-next-line no-undef
    ['pyramid_chart', $t('Pyramid chart'), IconHawkPyramidChart],
    // eslint-disable-next-line no-undef
    ['funnel_chart', $t('Funnel chart'), IconHawkFunnelChart],
    // eslint-disable-next-line no-undef
    ['pareto_chart', $t('Pareto chart'), IconHawkParetoChart],
    // eslint-disable-next-line no-undef
    ['waterfall_chart', $t('Waterfall chart'), IconHawkWaterfallChart],
    // eslint-disable-next-line no-undef
    ['number_chart', $t('Number chart'), IconHawkNumber],
    // eslint-disable-next-line no-undef
    ['details_card', $t('Details card'), IconHawkAutonumber],
    // eslint-disable-next-line no-undef
    ['map', $t('Map'), IconHawkMapOne],
  ].map((item) => {
    return {
      value: item[0],
      label: item[1],
      icon: item[2],
    };
  });
  // We wanted to add a weather widget at one point, but didn't add it. Found this library's pricing promising: https://weatherstack.com/pricing

  const all_columns = computed(() => {
    return Object.keys(widget_builder_data.value[0]);
  });

  const stack_by_field_columns = computed(() => {
    return all_columns.value.filter((column) => {
      return chart_builder_columns_data_types.value[column] === 'text';
    });
  });

  const fixed_markers_value_options = computed(() => {
    return all_columns.value.filter((column) => {
      if (widget_builder_config.value.chart.layout_values === column)
        return false;
      if (CHART_TO_VALUE_TYPE_MAP[widget_builder_config.value.chart.type].includes(chart_builder_columns_data_types.value[column]))
        return true;
      return false;
    });
  });

  function getMarkerValueOptions(existing_value) {
    const marker_value_options = cloneDeep(fixed_markers_value_options.value);
    if (!marker_value_options.includes(existing_value) && !Number.isNaN(Number.parseInt(existing_value)))
      marker_value_options.push(existing_value);
    return marker_value_options;
  }

  const date_bin_units = computed(() => {
    let category_column;
    widget_builder_config.value.query.stages.forEach((stage) => {
      if (!category_column)
        category_column = stage.columns.find(column => column.alias === widget_builder_config.value.chart.layout_category);
    });

    return category_column?.dateBin || category_column?.oldDateBin || 'day';
  });

  const date_bin_label = computed(() => {
    const bin_to_label_map = {
      day: $t('days'),
      week: $t('weeks'),
      month: $t('months'),
      quarter: $t('quarters'),
      year: $t('years'),
    };
    return bin_to_label_map[date_bin_units.value];
  });

  const min_value_for_gauge_and_progress = computed(() => {
    return Math.min(Number(widget_builder_data.value[0]?.[widget_builder_config.value.chart.layout_values]), 0);
  });

  const max_value_for_gauge_and_progress = computed(() => {
    return Math.max(Number(widget_builder_data.value[0]?.[widget_builder_config.value.chart.layout_values]) * 1.5, 100);
  });

  // Generates a chart given the data and config
  async function generateChart(id, data, config, chart_instance) {
    if (!config || !document.getElementById(id))
      return;

    logger.log('generateChartConfig: data:', data);
    logger.log('generateChartConfig: config:', config);

    const base_config = generateChartConfig(data, config);

    if (chart_instance) {
      chart_instance.dispose();
      chart_instance = null;
    }

    let chart_library;
    let new_chart_instance = null;

    if (base_config.metadata?.library !== 'fusioncharts') {
      chart_library = 'echarts';

      const chartElement = document.getElementById(id);
      new_chart_instance = echarts.init(chartElement, null, { renderer: 'svg' });

      const themed_config = applyEChartsUnifiedInterTheme(base_config, config);
      const formatted_config = applyClientSideFormatting(themed_config, config, new_chart_instance?.getWidth?.(), new_chart_instance?.getHeight?.());

      new_chart_instance.setOption(formatted_config);

      echarts_resize_observers.value[id] = new ResizeObserver(() => {
        new_chart_instance.resize();
        const resizedConfig = handleEchartsResize(formatted_config, config, new_chart_instance?.getWidth?.(), new_chart_instance?.getHeight?.());
        new_chart_instance.setOption(resizedConfig);
      });
      echarts_resize_observers.value[id].observe(chartElement);
    }

    if (base_config.metadata?.library === 'fusioncharts') {
      chart_library = 'fusioncharts';
      const themed_config = applyFusionUnifiedInterTheme(base_config, config);

      const { VueFusionChartsComponent, FusionCharts, Charts, FusionTheme, TimeSeries, Widgets, Maps, PowerCharts, ExcelExport } = await import_fusioncharts();
      VueFusionChartsComponent(FusionCharts, Charts, FusionTheme, TimeSeries, Widgets, Maps, PowerCharts, ExcelExport);

      new_chart_instance = await new Promise((resolve, reject) => {
        FusionCharts.ready(() => {
          try {
            const fusion_chart = new FusionCharts({
              type: themed_config.type,
              renderAt: id,
              width: '100%',
              height: '100%',
              dataFormat: 'json',
              dataSource: themed_config.dataSource,
            });

            fusion_chart.addEventListener('renderComplete', () => {
              resolve(fusion_chart);
            });

            fusion_chart.render();
          }
          catch (error) {
            reject(error);
          }
        });
      });
    }

    return {
      chart_instance: new_chart_instance,
      chart_library,
    };
  }

  // This function should always be called before passing the config to @sensehawk/chart-generator. Converts the form (vueform) compatible data into the format that the library accepts.
  async function convertFormDataToConfig(form_config, chart_instance) {
    function buildDataConfig() {
      const values = Array.isArray(form_config.chart.layout_values)
        ? form_config.chart.layout_values
        : [{ value: form_config.chart.layout_values }];

      const data_config = {
        category: form_config.chart.layout_category,
        values: values.map(item => item.value),
        ...(form_config.chart.overlap ? { overlap: true } : {}),
      };

      // Add stackBy for supported chart types
      const stackable_charts = ['column_chart', 'horizontalBar_chart', 'line_chart', 'area_chart', 'mixed_chart', 'heatmap_chart'];
      if (stackable_charts.includes(form_config.chart.type)) {
        data_config.stackBy = form_config.chart.stack_by === 'none' ? null : form_config.chart.stack_by;
      }

      const are_stacks_comparable = form_config.chart.layout_values.length === 1
        && ['column_chart', 'horizontalBar_chart', 'line_chart', 'area_chart', 'mixed_chart'].includes(form_config.chart.type)
        && !['none', true].includes(form_config.chart.stack_by);
      if (are_stacks_comparable) {
        data_config.compareStacks = form_config.chart.compare_stacks;
      }

      return data_config;
    }

    function buildInteractionsConfig() {
      const interactions = {};

      // Tooltip config for stacked charts
      if (form_config.chart.stack_by !== 'none') {
        interactions.tooltip = { trigger: 'item' };
      }

      // Data zoom for time series
      const time_series_charts = ['column_chart', 'line_chart', 'area_chart', 'mixed_chart'];
      if (
        time_series_charts.includes(form_config.chart.type)
        && form_config.chart.layout_category
        && chart_builder_columns_data_types.value[form_config.chart.layout_category] === 'date'
        && form_config.chart.timeseries
      ) {
        interactions.dataZoom = {
          enabled: true,
          type: form_config.chart.timeseries_mode,
        };
      }

      return interactions;
    }

    function buildSeriesConfig() {
      const series_charts = ['column_chart', 'horizontalBar_chart', 'line_chart', 'area_chart', 'mixed_chart', 'scatter_chart'];

      if (!form_config.chart.layout_values || !series_charts.includes(form_config.chart.type)) {
        return null;
      }

      return Object.values(form_config.chart.layout_values).reduce((acc, item) => {
        acc[item.value] = {
          name: item.legend || item.value,
          type: item.type,
          ...(form_config.chart.type !== 'horizontalBar_chart' ? { yAxisIndex: item.y_axis === 'primary' ? 0 : 1 } : {}),
          color: item.color,
          ...(!item.stack || form_config.chart.stack_by === 'none' ? { stack: false } : { stack: 'group1' }),
          lineColor: item.color,
          lineWidth: Number.parseInt(item.line_width),
          lineStyle: item.line_style,
          smooth: item.line_shape === 'curved',
          prefix: item.prefix || '',
          suffix: item.suffix || '',
          ...(item.type === 'bar' ? { opacity: item.opacity } : {}),
        };
        return acc;
      }, {});
    }

    function buildForecastConfig() {
      if (!form_config.chart.layout_values) {
        return null;
      }

      function buildForecastRateConfig(forecast_config) {
        const rate_config = {
          aggregation: forecast_config.rate.aggregation,
        };

        if (forecast_config.rate.rate_over !== 'all') {
          rate_config.direction = forecast_config.rate.rate_over;
          rate_config.duration = [forecast_config.rate.rate_over_number, forecast_config.bin_units];
        }

        return rate_config;
      }

      const forecasts = Object.values(form_config.chart.layout_values).reduce((acc, item) => {
        if (!item.forecast_config) {
          return acc;
        }

        acc[item.value] = {
          name: item.forecast_config.forecast_series_name,
          series: item.forecast_config.forecast_for_series,
          from: item.forecast_config.starting_from,
          ...((item.forecast_config.forecast_repetition.for_or_until === 'for')
            ? {
                length: item.forecast_config.forecast_repetition.forecast_for,
              }
            : {
                length: item.forecast_config.forecast_repetition.forecast_for,

                untilValue: item.forecast_config.forecast_repetition.until_value,
                untilDate: item.forecast_config.forecast_repetition.until_date,
              }),
          interval: item.forecast_config.bin_units,
          considerZero: item.forecast_config.consider_zeros,
          ...(item.forecast_config.rate
            ? (item.forecast_config.rate.aggregation === 'custom'
                ? { rate: Number.parseFloat(item.forecast_config.rate.custom_rate) }
                : { rate: buildForecastRateConfig(item.forecast_config) })
            : {}),
        };
        return acc;
      }, {});

      return Object.keys(forecasts).length ? Object.values(forecasts) : null;
    }

    function buildLayoutConfig() {
      return {
        title: form_config.chart.title,
        subtitle: form_config.chart.subtitle,
      };
    }

    function buildLegendConfig() {
      return {
        show: form_config.chart.legend !== 'hide',
        position: form_config.chart.legend,
      };
    }

    function buildDataValuesConfig() {
      return {
        show: form_config.chart.values === 'show',
        compact: form_config.chart.compact,
        precision: form_config.chart.precision,
        prefix: form_config.chart.prefix || '',
        suffix: form_config.chart.suffix || '',
      };
    }

    function buildAxesConfig() {
      const axes = {};

      // Axis names
      if (AXES_NAMES_SUPPORTED_CHARTS.includes(form_config.chart.type)) {
        axes.categoryName = form_config.chart.category_axis_name;
        axes.valueName = form_config.chart.value_axis_name;
        if (form_config.chart.secondary_y_axis) {
          axes.secondaryValueName = form_config.chart.secondary_y_axis;
        }
      }

      // Custom ranges and scales
      if (SERIES_SUPPORTED_CHARTS.includes(form_config.chart.type)) {
        Object.assign(axes, buildAxisRangesConfig(form_config));
        axes.valueScale = form_config.chart.primary_scale;
        axes.secondaryValueScale = form_config.chart.secondary_scale;
      }

      // Tick labels
      const tick_label_charts = [...SERIES_SUPPORTED_CHARTS, 'pareto_chart'];
      if (tick_label_charts.includes(form_config.chart.type)) {
        axes.categoryLabels = form_config.chart.category_tick_label;
        axes.valueLabels = form_config.chart.value_tick_label;
        axes.secondaryValueLabels = form_config.chart.secondary_value_tick_label;
      }

      return axes;
    }

    function buildAxisRangesConfig() {
      const ranges = {};

      // Primary axis ranges
      const primary_min = Number.parseInt(form_config.chart.custom_range_min);
      const primary_max = Number.parseInt(form_config.chart.custom_range_max);
      if (!Number.isNaN(primary_min))
        ranges.valueMin = primary_min;
      if (!Number.isNaN(primary_max))
        ranges.valueMax = primary_max;

      // Secondary axis ranges (for dual y-axis charts)
      if (DUAL_Y_AXIS_SUPPORTED_CHARTS.includes(form_config.chart.type)) {
        const secondary_min = Number.parseInt(form_config.chart.secondary_value_min);
        const secondary_max = Number.parseInt(form_config.chart.secondary_value_max);
        if (!Number.isNaN(secondary_min))
          ranges.secondaryValueMin = secondary_min;
        if (!Number.isNaN(secondary_max))
          ranges.secondaryValueMax = secondary_max;
      }

      return ranges;
    }

    function buildReferenceLinesConfig() {
      if (!form_config.chart.reference_lines) {
        return null;
      }

      const statistical_values = ['min', 'max', 'average', 'median', 'p25', 'p75', 'p90', 'p95'];

      return form_config.chart.reference_lines
        .map(line => ({
          value: statistical_values.includes(line.value) ? line.value : Number.parseFloat(line.value),
          label: line.label,
          color: line.color,
          series: line.series,
          lineStyle: line.line_style,
        }))
        .filter(line => line.value && line.series);
    }

    function buildChartSpecificConfig() {
      const chart_type = form_config.chart.type;

      if (chart_type === 'pareto_chart') {
        return buildParetoConfig(form_config);
      }
      else if (chart_type === 'heatmap_chart') {
        return buildHeatmapConfig(form_config);
      }
      else if (chart_type === 'waterfall_chart') {
        return buildWaterfallConfig(form_config);
      }
      else if (chart_type === 'pyramid_chart') {
        return buildPyramidConfig(form_config);
      }
      else if (chart_type === 'funnel_chart') {
        return buildFunnelConfig(form_config);
      }
      else if (['gauge_chart', 'progress_chart'].includes(chart_type)) {
        return buildGaugeProgressConfig(chart_type);
      }

      return null;
    }

    function buildParetoConfig() {
      return {
        pareto: {
          show80PercentLine: form_config.chart.show_eighty_percent_line,
          eightyPercentLineColor: form_config.chart.eighty_percent_line_color,
          eightyPercentLineStyle: form_config.chart.eighty_percent_line_style,
          barColor: form_config.chart.bar_color,
          lineColor: form_config.chart.cumulative_line_color,
        },
      };
    }

    function buildHeatmapConfig() {
      const heatmap_config = {
        colorScheme: BI_HEATMAP_PALETTES[form_config.chart.color_scheme]?.colors,
        colorType: form_config.chart.color_type,
      };

      if (form_config.chart.color_ranges?.length) {
        heatmap_config.colorPieces = form_config.chart.color_ranges.map(range => ({
          label: range.label,
          min: fixed_markers_value_options.value.includes(range.min) ? range.min : Number.parseInt(range.min),
          max: fixed_markers_value_options.value.includes(range.max) ? range.max : Number.parseInt(range.max),
          color: range.color,
        }));
      }

      return { heatmap: heatmap_config };
    }

    function buildWaterfallConfig() {
      return {
        waterfall: {
          showSum: form_config.chart.show_sum,
          positiveColor: form_config.chart.positive_color,
          negativeColor: form_config.chart.negative_color,
        },
      };
    }

    function buildPyramidConfig() {
      return {
        pyramid: {
          labelsAtCenter: form_config.chart.show_labels_at_center,
          showPercentages: form_config.chart.show_percentages,
          is3D: form_config.chart.render_in_3d,
          outline: form_config.chart.render_in_3d ? false : form_config.chart.outline,
        },
      };
    }

    function buildFunnelConfig() {
      return {
        funnel: {
          labelsAtCenter: form_config.chart.show_labels_at_center,
          showPercentages: form_config.chart.show_percentages,
          is3D: form_config.chart.render_in_3d,
          outline: form_config.chart.render_in_3d ? false : form_config.chart.outline,
          percentOfPrevious: form_config.chart.compare_with_previous,
          maintainSlope: form_config.chart.maintain_slope,
        },
      };
    }

    function buildGaugeProgressConfig(chart_type) {
      const config_key = chart_type.replace('_chart', '');
      const config = {
        color: form_config.chart.color || BI_DEFAULT_PALETTE_COLORS[0],
        min: !isNil(form_config.chart.minimum) ? Number(form_config.chart.minimum) : min_value_for_gauge_and_progress.value,
        max: !isNil(form_config.chart.maximum) ? Number(form_config.chart.maximum) : max_value_for_gauge_and_progress.value,
      };

      // Add markers if defined
      if (form_config.chart.markers) {
        config.markers = form_config.chart.markers.map(marker => ({
          label: marker.label,
          value: fixed_markers_value_options.value.includes(marker.value) ? marker.value : Number.parseInt(marker.value),
          color: marker.color,
        }));
      }

      // Add color ranges if defined
      if (form_config.chart.color_ranges?.length) {
        config.colorRange = form_config.chart.color_ranges.map(range => ({
          min: fixed_markers_value_options.value.includes(range.min) ? range.min : Number.parseInt(range.min),
          max: fixed_markers_value_options.value.includes(range.max) ? range.max : Number.parseInt(range.max),
          color: range.color,
        }));
      }

      return { [config_key]: config };
    }

    if (!is_chart_builder_form_valid.value) {
      chart_instance?.dispose?.();
      return;
    }
    await nextTick();

    const config = {
      type: form_config.chart.type.replace('_chart', ''),
      data: buildDataConfig(form_config),
      interactions: buildInteractionsConfig(form_config),
      layout: buildLayoutConfig(form_config),
      legend: buildLegendConfig(form_config),
      dataValues: buildDataValuesConfig(form_config),
      axes: buildAxesConfig(form_config),
    };

    // Add series config for series supported charts
    const series_config = buildSeriesConfig(form_config);
    if (series_config) {
      config.series = series_config;
    }

    // Add forecast config if defined
    const forecast_config = buildForecastConfig(form_config);
    if (forecast_config) {
      config.data.generateForecast = forecast_config;
    }

    // Add reference lines if defined
    const reference_lines = buildReferenceLinesConfig(form_config);
    if (reference_lines) {
      config.reference_lines = reference_lines;
    }

    // Add chart-specific configuration
    const chart_specific = buildChartSpecificConfig(form_config);
    if (chart_specific) {
      config.chartSpecific = chart_specific;
    }

    return config;
  }

  // This function should always be called after fetching the stored config (the library compatible one) before parsing/loading them into the form data.
  function parseConfigToFormData(original_config) {
    const config = cloneDeep(original_config);
    const chart = {
      type: `${config.type}_chart`,
    };

    function parseLayoutData() {
      if (!config.data)
        return;

      chart.layout_category = config.data.category;
      chart.layout_values = SERIES_SUPPORTED_CHARTS.includes(chart.type)
        ? config.data.values
        : config.data.values[0];
      chart.stack_by = config.data.stackBy || 'none';
      chart.compare_stacks = config.data.compareStacks || false;
      chart.overlap = config.data.overlap || false;
    }

    function parseSeriesConfig() {
      if (!config.series)
        return;

      function parseForecastConfig(forecast_config) {
        return {
          forecast_series_name: forecast_config?.name || '',
          forecast_for_series: forecast_config?.series || '',
          starting_from: forecast_config?.from || 'lastDataPoint',
          forecast_repetition: {
            for_or_until: forecast_config.untilValue ? 'until' : 'for',
            ...(forecast_config.untilValue
              ? { until_value: forecast_config.untilValue, until_date: forecast_config.untilDate, forecast_for: 1 }
              : { forecast_for: forecast_config.length }),
          },
          consider_zeros: forecast_config?.considerZero || false,
          bin_units: forecast_config?.interval || 'day',
          ...(forecast_config?.rate
            ? {
                rate: !Number.isNaN(Number.parseFloat(forecast_config.rate))
                  ? {
                      aggregation: 'custom',
                      custom_rate: Number.parseFloat(forecast_config.rate),
                    }
                  : {
                      aggregation: forecast_config?.rate?.aggregation || 'average',
                      rate_over: forecast_config?.rate?.direction || 'all',
                      rate_over_number: forecast_config?.rate?.duration?.[0] || 1,
                    },
              }
            : {}),
        };
      }

      const layout_values = [];
      Object.entries(config.series).forEach(([key, series]) => {
        const forecast_config = config.data.generateForecast?.find?.(
          forecast => forecast.name === key,
        );

        layout_values.push({
          value: key,
          legend: series.name,
          type: series.type,
          y_axis: series.yAxisIndex === 1 ? 'secondary' : 'primary',
          color: series.color,
          stack: config.data.stackBy === null ? true : (series.stack !== false),
          line_width: series.lineWidth || 2,
          line_style: series.lineStyle || 'solid',
          line_shape: series.smooth ? 'curved' : 'straight',
          prefix: series.prefix || '',
          suffix: series.suffix || '',
          ...(series.type === 'bar' ? { opacity: isNil(series.opacity) ? 1 : series.opacity } : {}),
          ...(forecast_config ? { forecast_config: parseForecastConfig(forecast_config) } : {}),
        });
      });

      chart.layout_values = layout_values;
    }

    function parseDisplayConfig() {
      if (config.layout) {
        chart.title = config.layout.title;
        chart.subtitle = config.layout.subtitle;
      }

      if (config.legend) {
        chart.legend = config.legend.show ? config.legend.position : 'hide';
      }

      if (config.dataValues) {
        chart.values = config.dataValues.show ? 'show' : 'hide';
        chart.compact = config.dataValues.compact;
        chart.precision = config.dataValues.precision;
        chart.prefix = config.dataValues.prefix || '';
        chart.suffix = config.dataValues.suffix || '';
      }
    }

    function parseAxesConfig() {
      if (!config.axes)
        return;

      chart.category_axis_name = config.axes.categoryName;
      chart.value_axis_name = config.axes.valueName;
      chart.secondary_y_axis = config.axes.secondaryValueName;
      chart.custom_range_min = config.axes.valueMin;
      chart.custom_range_max = config.axes.valueMax;
      chart.secondary_value_min = config.axes.secondaryValueMin;
      chart.secondary_value_max = config.axes.secondaryValueMax;
      chart.category_tick_label = config.axes.categoryLabels;
      chart.value_tick_label = config.axes.valueLabels;
      chart.secondary_value_tick_label = config.axes.secondaryValueLabels;
      chart.primary_scale = config.axes.valueScale;
      chart.secondary_scale = config.axes.secondaryValueScale;
    }

    function parseInteractionsConfig() {
      if (config.interactions?.dataZoom) {
        chart.timeseries = config.interactions.dataZoom.enabled;
        chart.timeseries_mode = config.interactions.dataZoom.type;
      }
    }

    function parseReferenceLinesConfig() {
      if (!config.reference_lines)
        return;

      chart.reference_lines = config.reference_lines.map(line => ({
        value: line.value.toString(),
        label: line.label,
        color: line.color,
        series: line.series,
        line_style: line.lineStyle,
      }));
    }

    function parseChartSpecificConfig() {
      if (!config.chartSpecific)
        return;

      const specific = config.chartSpecific;

      if (specific.pareto) {
        parseParetoConfig(specific.pareto);
      }

      if (specific.heatmap) {
        parseHeatmapConfig(specific.heatmap);
      }

      if (specific.waterfall) {
        parseWaterfallConfig(specific.waterfall);
      }

      if (specific.pyramid) {
        parsePyramidConfig(specific.pyramid);
      }

      if (specific.funnel) {
        parseFunnelConfig(specific.funnel);
      }

      if (specific.gauge || specific.progress) {
        parseGaugeProgressConfig(specific.gauge || specific.progress);
      }
    }

    function parseParetoConfig(pareto) {
      chart.show_eighty_percent_line = pareto.show80PercentLine;
      chart.eighty_percent_line_color = pareto.eightyPercentLineColor;
      chart.eighty_percent_line_style = pareto.eightyPercentLineStyle;
      chart.bar_color = pareto.barColor;
      chart.cumulative_line_color = pareto.lineColor;
    }

    function parseHeatmapConfig(heatmap) {
      chart.color_scheme = Object.keys(BI_HEATMAP_PALETTES).find(
        key => isEqual(BI_HEATMAP_PALETTES[key]?.colors, heatmap.colorScheme),
      );
      chart.color_type = heatmap.colorType;

      if (heatmap.colorPieces) {
        chart.color_ranges = heatmap.colorPieces.map(piece => ({
          label: piece.label,
          min: piece.min.toString(),
          max: piece.max.toString(),
          color: piece.color,
        }));
      }
    }

    function parseWaterfallConfig(waterfall) {
      chart.show_sum = waterfall.showSum;
      chart.positive_color = waterfall.positiveColor;
      chart.negative_color = waterfall.negativeColor;
    }

    function parsePyramidConfig(pyramid) {
      chart.show_labels_at_center = pyramid.labelsAtCenter;
      chart.show_percentages = pyramid.showPercentages;
      chart.render_in_3d = pyramid.is3D;
      chart.outline = pyramid.is3D ? false : pyramid.outline;
    }

    function parseFunnelConfig(funnel) {
      chart.show_labels_at_center = funnel.labelsAtCenter;
      chart.show_percentages = funnel.showPercentages;
      chart.render_in_3d = funnel.is3D;
      chart.outline = funnel.is3D ? false : funnel.outline;
      chart.compare_with_previous = funnel.percentOfPrevious;
      chart.maintain_slope = funnel.maintainSlope;
    }

    function parseGaugeProgressConfig(gaugeConfig) {
      chart.color = gaugeConfig.color;
      chart.minimum = gaugeConfig.min;
      chart.maximum = gaugeConfig.max;

      if (gaugeConfig.markers) {
        chart.markers = gaugeConfig.markers.map(marker => ({
          label: marker.label,
          value: marker.value.toString(),
          color: marker.color,
        }));
      }

      if (gaugeConfig.colorRange) {
        chart.color_ranges = gaugeConfig.colorRange.map(range => ({
          min: range.min.toString(),
          max: range.max.toString(),
          color: range.color,
        }));
      }
    }

    parseLayoutData();
    parseSeriesConfig();
    parseDisplayConfig();
    parseAxesConfig();
    parseInteractionsConfig();
    parseReferenceLinesConfig();
    parseChartSpecificConfig();

    return { chart };
  }

  return {
    chart_types,
    all_columns,
    stack_by_field_columns,
    getMarkerValueOptions,
    generateChart,
    convertFormDataToConfig,
    parseConfigToFormData,
    min_value_for_gauge_and_progress,
    max_value_for_gauge_and_progress,
    date_bin_units,
    date_bin_label,
  };
}
